import { ChannelType, channelTypeHirarchie } from "../../../src/models/ChannelType";
import { itx } from "../../../src/translationItx/itx";
import React, { useId, useState } from "react";
import type { C1_ButtonColor, C1_ButtonOrientation } from "../types/C1_EloTypes";
import { ButtonRowModeControl } from "./ButtonRowModeControl";
import { ButtonSelectionOverlay } from "./ButtonSelectionOverlay";
import { fitSvgLabel } from "../../../src/components/fitSvgLabel";
import { buttonContentBox } from "./buttonContentBox";

/** Groups channel types only for icon and fallback-label rendering. */
function iconTypeForChannel(channelType: ChannelType): ChannelType {
  if (channelTypeHirarchie("isRollo")?.includes(channelType)) return ChannelType.Jalousie;
  if (channelTypeHirarchie("isHeating")?.includes(channelType) || channelType === ChannelType.OperationMode) return ChannelType.Heating;
  if (
    channelType === ChannelType.Dimmer ||
    channelType === ChannelType.TuneableWhite ||
    channelType === ChannelType.Rgb
  ) {
    return ChannelType.Dimmer;
  }
  if (channelTypeHirarchie("isLight")?.includes(channelType) || channelType === ChannelType.PlugSwitchable) return ChannelType.Switch;
  if (channelType === ChannelType.Scene) return ChannelType.Scene;
  return ChannelType.Unknown;
}


export interface DisplayFunction {
  channelType: ChannelType;
  name?: string;
  active?: boolean;
}

export interface RockerRowConfig {
  mode: "shared" | "split";
  shared?: DisplayFunction;
  left?: DisplayFunction;
  right?: DisplayFunction;
}

export interface GlassRockerDisplaySvgProps {
  rows: RockerRowConfig[];
  /** Mischt einen dezenten Auswahlton direkt in die Darstellung ein. */
  selected?: boolean;
  /** Physische Breite des Tasters in Millimetern. */
  width?: number;
  /** Physische Höhe des Tasters in Millimetern. */
  height?: number;
  /** Rundet ausschließlich die äußeren Ecken des Glasgehäuses. */
  roundedCorners?: boolean;
  onButtonClick?: (rowIndex: number, side: "left" | "right") => void;
  /** Associates each visible key with its model channel for connection anchors. */
  getButtonChannelId?: (rowIndex: number, side: "left" | "right") => string | undefined;
  /** Model channel whose matching key surfaces receive a light orange overlay. */
  selectedButtonChannel?: { readonly id: string };
  displayedButtonChannelIds?: ReadonlySet<string>;
  onNameChange?: (rowIndex: number, target: "shared" | "left" | "right", name: string) => void;
  className?: string;
  style?: React.CSSProperties;
  onRowModeChange?: (rowIndex: number, mode: "shared" | "split") => void;
  /** false uses SVG user units, useful when nested in another SVG. */
  usePhysicalSize?: boolean;
  x?: number;
  y?: number;
  /** Dreht und entspiegelt nur den Inhalt innerhalb des unveränderten Rahmens. */
  contentRotation?: number;
  contentReflected?: boolean;
  color?: C1_ButtonColor;
  orientation?: C1_ButtonOrientation;
  hasDisplay?: boolean;
  /** Hides synthetic display content while keeping the physical key geometry. */
  showDisplayOverlay?: boolean;
  /** Editable labels inside physical keys, for generic devices without a display. */
  showButtonLabels?: boolean;
  constrainLabels?: boolean;
}

const FUNCTION_INFO: Partial<Record<ChannelType, { title: string }>> = {
  [ChannelType.Heating]: { get title() { return itx("panelDataImport.heating", "Heizen"); } },
  [ChannelType.Switch]: { get title() { return itx("SignalTypes.short.Switch", "Schalten"); } },
  [ChannelType.Dimmer]: { get title() { return itx("channelType.Dimmer", "Dimmen"); } },
  [ChannelType.Jalousie]: { get title() { return itx("toolBar.jalousieShort", "Jalousie"); } },
  [ChannelType.Scene]: { get title() { return itx("commandUi.scene", "Szene"); } },
  [ChannelType.Unknown]: { get title() { return itx("SignalTypes.short.Unknown", "Unbekannt"); } },
};

export function buttonFunctionTitle(channelType: ChannelType): string {
  return (FUNCTION_INFO[iconTypeForChannel(channelType)] ?? FUNCTION_INFO[ChannelType.Unknown]!).title;
}

function FunctionIcon({
  channelType,
  x,
  y,
  size,
  color,
}: {
  channelType: ChannelType;
  x: number;
  y: number;
  size: number;
  color: string;
}) {
  const iconType = iconTypeForChannel(channelType);
  const transform = `translate(${x - size / 2} ${y - size / 2}) scale(${size / 24})`;
  const common = {
    fill: "none",
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  return (
    <g transform={transform} pointerEvents="none">
      {iconType === ChannelType.Heating && (
        <>
          <path {...common} d="M8 19c-2-2.2 2-3.8 0-6s2-3.8 0-6" />
          <path {...common} d="M12 19c-2-2.2 2-3.8 0-6s2-3.8 0-6" />
          <path {...common} d="M16 19c-2-2.2 2-3.8 0-6s2-3.8 0-6" />
        </>
      )}
      {iconType === ChannelType.Switch && (
        <>
          <circle {...common} cx="12" cy="12" r="7" />
          <path {...common} d="M12 4v8" />
        </>
      )}
      {iconType === ChannelType.Dimmer && (
        <>
          <circle {...common} cx="12" cy="12" r="4" />
          <path {...common} d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
        </>
      )}
      {iconType === ChannelType.Jalousie && (
        <>
          <path {...common} d="M5 5h14M7 8h10M7 11h10M7 14h10M7 17h10" />
          <path {...common} d="m4 10-2 2 2 2M20 10l2 2-2 2" />
        </>
      )}
      {iconType === ChannelType.Scene && (
        <path {...common} d="m12 3 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z" />
      )}
      {iconType === ChannelType.Unknown && (
        <>
          <circle {...common} cx="12" cy="12" r="8" />
          <text x="12" y="16" textAnchor="middle" fill={color} fontSize="12" fontWeight="600" stroke="none">
            ?
          </text>
        </>
      )}
    </g>
  );
}

export function GlassRockerDisplaySvg({
  rows,
  selected = false,
  width = 92,
  height = 92,
  roundedCorners = false,
  onButtonClick,
  getButtonChannelId,
  selectedButtonChannel,
  displayedButtonChannelIds,
  onNameChange,
  className,
  style,
  onRowModeChange,
  usePhysicalSize = true,
  x,
  y,
  contentRotation = 0,
  contentReflected = false,
  color = "white",
  orientation = "vertical",
  hasDisplay = true,
  showDisplayOverlay = true,
  showButtonLabels = false,
  constrainLabels = false,
}: GlassRockerDisplaySvgProps) {
  const [editingName, setEditingName] = useState<string | null>(null);
  const uid = useId().replace(/:/g, "");
  const panelGradient = `${uid}-panel`;
  const displayGradient = `${uid}-display`;
  const glassReflection = `${uid}-glass-reflection`;
  const panelClip = `${uid}-panel-clip`;
  const shadow = `${uid}-shadow`;
  const isBlack = color === "black";

  const designWidth = 220;
  const designHeight = designWidth * (height / width);
  const outerRadius = roundedCorners ? 6 : 0;
  const outer = 7;
  const contentInset = 8;
  const rowGap = 7;
  const displayCellInset = 3;
  const rowHeight =
    (designHeight - outer * 2 - contentInset * 2 - Math.max(0, rows.length - 1) * rowGap) / Math.max(1, rows.length);
  const buttonWidth = 42;
  const sideMargin = 9;
  const middleGap = 8;
  const orientationRotation = orientation === "vertical" ? -90 : 0;
  const contentTransform = `translate(${designWidth / 2} ${designHeight / 2}) scale(${contentReflected ? -1 : 1} 1) rotate(${orientationRotation - contentRotation}) translate(${-designWidth / 2} ${-designHeight / 2})`;

  const leftX = outer + sideMargin;
  const rightX = designWidth - outer - sideMargin - buttonWidth;
  const displayX = leftX + buttonWidth + middleGap;
  const displayW = rightX - middleGap - displayX;
  const controlLeftX = hasDisplay ? leftX : outer + contentInset;
  const controlButtonWidth = hasDisplay ? buttonWidth : (designWidth - 2 * (outer + contentInset) - middleGap) / 2;
  const controlRightX = hasDisplay ? rightX : controlLeftX + controlButtonWidth + middleGap;

  const renderFunction = (
    fn: DisplayFunction | undefined,
    areaX: number,
    areaY: number,
    areaWidth: number,
    areaHeight: number,
    rowIndex: number,
    target: "shared" | "left" | "right",
    labelOnly = false,
  ) => {
    if (!fn) return null;
    const { x, y, width: w, height: h, transform: labelTransform } =
      buttonContentBox(areaX, areaY, areaWidth, areaHeight, orientation);
    const color = fn.active ? "#38a9e8" : isBlack ? "#e0e5e8" : "#334155";
    const title = fn.name?.trim() || buttonFunctionTitle(fn.channelType);
    const fontSize = w < 55 ? 6.8 : 9.5;
    const fitted = constrainLabels ? fitSvgLabel(title, w - 8, fontSize) : undefined;
    const editKey = `${rowIndex}-${target}`;
    const channelId = getButtonChannelId?.(rowIndex, target === "right" ? "right" : "left");
    const canEditName = !!onNameChange && !!channelId
      && (selectedButtonChannel?.id === channelId || !!displayedButtonChannelIds?.has(channelId));
    const isEditing = canEditName && editingName === editKey;
    const editHeight = Math.max(22, h * 0.22);

    return (
      <g transform={labelTransform}>
        {!isEditing && (
          <text
            x={x + w / 2}
            y={y + h * (labelOnly ? 0.5 : 0.3)}
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="Arial, Helvetica, sans-serif"
            fontSize={fontSize}
            textLength={fitted?.text ? fitted.width : undefined}
            lengthAdjust={fitted ? "spacingAndGlyphs" : undefined}
            fontWeight={600}
            fill={color}
            onClick={(event) => {
              event.stopPropagation();
              if (canEditName) setEditingName(editKey);
              else onButtonClick?.(rowIndex, target === "right" ? "right" : "left");
            }}
            style={{ cursor: canEditName ? "text" : "default" }}
          >
            {fitted ? fitted.text : title}
          </text>
        )}
        {isEditing && (
          <foreignObject x={x + 2} y={y + h * (labelOnly ? 0.5 : 0.3) - editHeight / 2} width={w - 4} height={editHeight}>
            <input
              autoFocus
              aria-label={itx("c1.ui.Tastenname", "Tastenname")}
              value={fn.name ?? ""}
              placeholder={buttonFunctionTitle(fn.channelType)}
              onChange={(event) => onNameChange?.(rowIndex, target, event.target.value)}
              onClick={(event) => event.stopPropagation()}
              onFocus={(event) => event.currentTarget.select()}
              onBlur={() => setEditingName(null)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === "Escape") {
                  event.currentTarget.blur();
                }
              }}
              style={{
                width: "100%",
                height: "100%",
                boxSizing: "border-box",
                padding: 0,
                border: "1px solid rgba(115, 206, 255, .75)",
                outline: 0,
                background: isBlack ? "rgba(7, 16, 20, .9)" : "rgba(255, 255, 255, .92)",
                color,
                textAlign: "center",
                fontFamily: "Arial, Helvetica, sans-serif",
                fontSize: "var(--dock-text-size, 14px)",
                lineHeight: "normal",
                fontWeight: 600,
              }}
            />
          </foreignObject>
        )}
        {!labelOnly && <FunctionIcon channelType={fn.channelType} x={x + w / 2} y={y + h * 0.66} size={w < 55 ? 15 : 20} color={color} />}
      </g>
    );
  };

  const rowElements = rows.map((row, i) => {
    const y = outer + contentInset + i * (rowHeight + rowGap);
    const shared = row.mode === "shared";
    const leftWidth = shared ? controlRightX + controlButtonWidth - controlLeftX : controlButtonWidth;
    const leftActive = row.mode === "shared" ? !!row.shared?.active : !!row.left?.active;
    const rightActive = row.mode === "shared" ? !!row.shared?.active : !!row.right?.active;

    return (
      <g key={i} data-connection-control-region="" data-connection-shared-rocker={row.mode === "shared" ? "" : undefined}>
        <rect
          x={controlLeftX}
          data-connection-object-id={getButtonChannelId?.(i, "left")}
          data-connection-exit-x={-1}
          y={y}
          width={leftWidth}
          height={rowHeight}
          rx={4}
          fill={leftActive ? "#354750" : isBlack ? "#1b2023" : "#f7f7f5"}
          stroke={leftActive ? "#8298a6" : isBlack ? "#454a4e" : "#b7b7b4"}
          strokeWidth=".3"
          strokeOpacity=".22"
          onClick={() => onButtonClick?.(i, "left")}
          style={{ cursor: "default" }}
        />

        {!shared && <rect
          x={controlRightX}
          data-connection-object-id={getButtonChannelId?.(i, "right")}
          data-connection-exit-x={1}
          y={y}
          width={controlButtonWidth}
          height={rowHeight}
          rx={4}
          fill={rightActive ? "#354750" : isBlack ? "#1b2023" : "#f7f7f5"}
          stroke={rightActive ? "#8298a6" : isBlack ? "#454a4e" : "#b7b7b4"}
          strokeWidth=".3"
          strokeOpacity=".22"
          onClick={() => onButtonClick?.(i, "right")}
          style={{ cursor: "default" }}
        />}

        <ButtonSelectionOverlay
          selected={(!!selectedButtonChannel && selectedButtonChannel.id === getButtonChannelId?.(i, "left"))
            || !!displayedButtonChannelIds?.has(getButtonChannelId?.(i, "left") ?? "")}
          x={controlLeftX} y={y} width={leftWidth} height={rowHeight}
        />
        {!shared && <ButtonSelectionOverlay
          selected={(!!selectedButtonChannel && selectedButtonChannel.id === getButtonChannelId?.(i, "right"))
            || !!displayedButtonChannelIds?.has(getButtonChannelId?.(i, "right") ?? "")}
          x={controlRightX} y={y} width={controlButtonWidth} height={rowHeight}
        />}

        {!(showButtonLabels && !hasDisplay) && <circle
          cx={controlLeftX + (shared && !hasDisplay ? leftWidth : controlButtonWidth) / 2}
          cy={y + rowHeight / 2}
          r={2.1}
          fill={leftActive ? "#73ceff" : "#e0e4e6"}
          pointerEvents="none"
        />}

        {!shared && !(showButtonLabels && !hasDisplay) && <circle
          cx={controlRightX + controlButtonWidth / 2}
          cy={y + rowHeight / 2}
          r={2.1}
          fill={rightActive ? "#73ceff" : "#e0e4e6"}
          pointerEvents="none"
        />}

        {!hasDisplay && showButtonLabels && (shared
          ? renderFunction(row.shared, controlLeftX, y, leftWidth, rowHeight, i, "shared", true)
          : <>
            {renderFunction(row.left, controlLeftX, y, controlButtonWidth, rowHeight, i, "left", true)}
            {renderFunction(row.right, controlRightX, y, controlButtonWidth, rowHeight, i, "right", true)}
          </>)}

        {hasDisplay && showDisplayOverlay && (
          <>
            <rect
              x={displayX + displayCellInset}
              y={y + displayCellInset}
              width={displayW - displayCellInset * 2}
              height={rowHeight - displayCellInset * 2}
              rx={1.5}
              fill={isBlack ? "#071014" : "#ffffff"}
              fillOpacity={isBlack ? 0.08 : 0.3}
              stroke={isBlack ? "#b8c8ce" : "#64748b"}
              strokeOpacity={isBlack ? 0.18 : 0.28}
              strokeWidth=".3"
            />

            {row.mode === "shared" ? (
              renderFunction(
                row.shared,
                displayX + displayCellInset,
                y + displayCellInset,
                displayW - displayCellInset * 2,
                rowHeight - displayCellInset * 2,
                i,
                "shared",
              )
            ) : (
              <>
                <line
                  x1={displayX + displayW / 2}
                  y1={y + displayCellInset + 4}
                  x2={displayX + displayW / 2}
                  y2={y + rowHeight - displayCellInset - 4}
                  stroke={isBlack ? "#5b6469" : "#94a3b8"}
                />
                {renderFunction(
                  row.left,
                  displayX + displayCellInset,
                  y + displayCellInset,
                  displayW / 2 - displayCellInset,
                  rowHeight - displayCellInset * 2,
                  i,
                  "left",
                )}
                {renderFunction(
                  row.right,
                  displayX + displayW / 2,
                  y + displayCellInset,
                  displayW / 2 - displayCellInset,
                  rowHeight - displayCellInset * 2,
                  i,
                  "right",
                )}
              </>
            )}
          </>
        )}
        <ButtonRowModeControl
          x={designWidth / 2}
          y={y + rowHeight - 6}
          isSplit={row.mode === "split"}
          rowIndex={i}
          onToggle={
            onRowModeChange ? () => onRowModeChange(i, row.mode === "split" ? "shared" : "split") : undefined
          }
        />
      </g>
    );
  });

  return (
    <svg
      viewBox={`0 0 ${designWidth} ${designHeight}`}
      x={x}
      y={y}
      width={usePhysicalSize ? `${width}mm` : width}
      height={usePhysicalSize ? `${height}mm` : height}
      className={className}
      style={{ maxWidth: "100%", height: "auto", ...style }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={panelGradient} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={isBlack ? "#373c40" : "#ffffff"} />
          <stop offset="18%" stopColor={isBlack ? "#17191b" : "#f4f4f2"} />
          <stop offset="72%" stopColor={isBlack ? "#08090a" : "#dededb"} />
          <stop offset="100%" stopColor={isBlack ? "#1b1d1f" : "#eeeeec"} />
        </linearGradient>

        <linearGradient
          id={displayGradient}
          x1="0"
          y1="0"
          x2="1"
          y2="1"
          gradientTransform={orientation === "vertical" ? "rotate(90 .5 .5)" : undefined}
        >
          <stop offset="0%" stopColor={isBlack ? "#607984" : "#ffffff"} />
          <stop offset="45%" stopColor={isBlack ? "#405a64" : "#e2e8f0"} />
          <stop offset="100%" stopColor={isBlack ? "#58717a" : "#f8fafc"} />
        </linearGradient>

        <linearGradient
          id={glassReflection}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2={designWidth}
          y2={designHeight}
          gradientTransform={`rotate(-10 ${designWidth / 2} ${designHeight / 2})`}
        >
          {isBlack ? (
            <>
              <stop offset="0%" stopColor="#ffffff" stopOpacity=".3" />
              <stop offset="46%" stopColor="#ffffff" stopOpacity=".3" />
              <stop offset="52%" stopColor="#ffffff" stopOpacity=".13" />
              <stop offset="58%" stopColor="#ffffff" stopOpacity="0" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </>
          ) : (
            <>
              <stop offset="0%" stopColor="#ffffff" stopOpacity=".08" />
              <stop offset="38%" stopColor="#ffffff" stopOpacity=".28" />
              <stop offset="46%" stopColor="#ffffff" stopOpacity=".72" />
              <stop offset="50%" stopColor="#94a3b8" stopOpacity=".2" />
              <stop offset="55%" stopColor="#64748b" stopOpacity=".12" />
              <stop offset="62%" stopColor="#64748b" stopOpacity="0" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </>
          )}
        </linearGradient>

        <clipPath id={panelClip}>
          <rect
            x={outer}
            y={outer}
            width={designWidth - outer * 2}
            height={designHeight - outer * 2}
            rx={outerRadius}
          />
        </clipPath>

        <filter id={shadow} x="-25%" y="-25%" width="150%" height="150%">
          <feDropShadow dx="0" dy="2.5" stdDeviation="2.5" floodColor="#000" floodOpacity=".38" />
        </filter>
      </defs>

      <rect
        x={outer}
        y={outer}
        width={designWidth - outer * 2}
        height={designHeight - outer * 2}
        rx={outerRadius}
        fill={`url(#${panelGradient})`}
        stroke={isBlack ? "#50565b" : "#b7b7b2"}
        filter={`url(#${shadow})`}
      />

      <g transform={contentTransform}>
        {hasDisplay && (
          <rect
            x={displayX}
            y={outer + contentInset}
            width={displayW}
            height={rows.length * rowHeight + Math.max(0, rows.length - 1) * rowGap}
            rx={0}
            fill={`url(#${displayGradient})`}
            stroke={isBlack ? "#526068" : "#94a3b8"}
            strokeOpacity=".22"
          />
        )}

        {rowElements}
      </g>

      <rect
        x={outer}
        y={outer}
        width={designWidth - outer * 2}
        height={designHeight - outer * 2}
        fill={`url(#${glassReflection})`}
        opacity={0.6}
        clipPath={`url(#${panelClip})`}
        pointerEvents="none"
      />
      {selected && (
        <rect
          data-button-device-selection="true"
          x={outer}
          y={outer}
          width={designWidth - outer * 2}
          height={designHeight - outer * 2}
          rx={outerRadius}
          fill="#ffbe73"
          fillOpacity={0.3}
          pointerEvents="none"
        />
      )}
    </svg>
  );
}

export default GlassRockerDisplaySvg;
